import type { ReactNode } from "react";
import { Battery, ChevronUp, Wrench } from "lucide-react";
import type { BessConfig, SiteSnapshot } from "@/data/types";
import { Card } from "@/components/ui/Card";
import { HelpHint } from "@/components/ui/HelpHint";
import { useGlossary } from "@/lib/glossary";
import { useT } from "@/lib/i18n";
import { cyclesFromThroughput } from "@/lib/format";

/**
 * Battery health — honest, observation-only metrics derived from data we
 * actually have on hand. No warranty extrapolation, no fabricated cycle
 * projections.
 *
 * Tiles:
 *   - Cycles today (full-cycle: today_throughput / 2·total_capacity, via
 *     the shared cyclesFromThroughput helper so the throughput chart's
 *     KPI can never disagree) — accurate when the EMS server has written
 *     the per-BESS daily energy counters.
 *   - DC bus spread (max − min across online units) — early indicator of
 *     individual cabinet drift.
 *   - Ambient temperature average + spread — cell-aging warning sign.
 *   - Online ratio.
 */
export function SoHPanel({
  snapshot,
  bess,
}: {
  snapshot: SiteSnapshot;
  bess: BessConfig[];
}) {
  const t = useT();
  const glossary = useGlossary();
  const totalCap = bess.reduce((s, b) => s + b.capacity_kWh, 0) || 1;
  const todayThroughput = snapshot.energy_today.bess_charge_kWh + snapshot.energy_today.bess_discharge_kWh;
  const cyclesToday = cyclesFromThroughput(todayThroughput, totalCap);

  const dcBusAvg = snapshot.bess_aggregate.total_voltage_V;
  const tempAvg = snapshot.bess_aggregate.avg_temperature_C;
  const onlineUnits = snapshot.bess_per_unit.filter((u) => u.online);
  const dcVMin = onlineUnits.length ? Math.min(...onlineUnits.map((u) => u.total_voltage_V)) : 0;
  const dcVMax = onlineUnits.length ? Math.max(...onlineUnits.map((u) => u.total_voltage_V)) : 0;
  const dcSpread = dcVMax - dcVMin;
  const tempMin = onlineUnits.length ? Math.min(...onlineUnits.map((u) => u.ambient_temperature_C)) : 0;
  const tempMax = onlineUnits.length ? Math.max(...onlineUnits.map((u) => u.ambient_temperature_C)) : 0;

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-text-faint font-semibold">
            <Wrench size={12} /> {t("analyze.soh.title")}
          </div>
          <p className="mt-0.5 text-xs text-text-muted">
            {t("analyze.soh.caption")}
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          icon={Battery}
          label={t("analyze.soh.cyclesToday")}
          hint={<HelpHint title={glossary.cycle.term}>{glossary.cycle.def}</HelpHint>}
          value={Number.isFinite(cyclesToday) ? cyclesToday.toFixed(2) : "—"}
          sub={todayThroughput > 0 ? t("analyze.soh.throughputSub", { kwh: todayThroughput.toFixed(0) }) : t("analyze.soh.noThroughput")}
        />
        <Tile
          icon={ChevronUp}
          label={t("analyze.soh.dcSpread")}
          hint={<HelpHint title={glossary.dcBus.term}>{glossary.dcBus.def}</HelpHint>}
          value={dcSpread > 0 ? `${dcSpread.toFixed(1)} V` : "—"}
          sub={dcBusAvg > 0 ? t("analyze.soh.dcAvgSub", { avg: dcBusAvg.toFixed(0), min: dcVMin.toFixed(0), max: dcVMax.toFixed(0) }) : t("analyze.soh.noDc")}
        />
        <Tile
          icon={Wrench}
          label={t("analyze.soh.ambientTemp")}
          hint={
            <HelpHint title={t("analyze.soh.tempHelpTitle")}>
              {t("analyze.soh.tempHelp")}
            </HelpHint>
          }
          value={Number.isFinite(tempAvg) ? `${tempAvg.toFixed(1)} °C` : "—"}
          sub={onlineUnits.length > 1 ? t("analyze.soh.tempSpreadSub", { d: (tempMax - tempMin).toFixed(1) }) : t("analyze.soh.singleUnit")}
        />
        <Tile
          icon={Battery}
          label={t("analyze.soh.onlineUnits")}
          value={`${onlineUnits.length}/${bess.length}`}
          sub={onlineUnits.length === bess.length ? t("analyze.soh.allResponding") : t("analyze.soh.investigateOffline")}
        />
      </div>
    </Card>
  );
}

function Tile({ icon: Icon, label, value, sub, hint }: { icon: any; label: string; value: string; sub?: string; hint?: ReactNode }) {
  return (
    <div className="rounded-lg border border-edge bg-surface-2 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-faint font-semibold">
        <Icon size={11} /> {label}
        {hint}
      </div>
      <div className="mt-1 text-xl font-bold num">{value}</div>
      {sub && <div className="text-[10px] text-text-muted">{sub}</div>}
    </div>
  );
}
